import { Link } from '@solidjs/router';
import { createSignal, Match, Switch } from 'solid-js';
import { DOMElement } from 'solid-js/jsx-runtime';
import clsx from 'clsx';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { FacebookIcon } from '../../icons-library/social-media/facebook';
import { InstagramIcon } from '../../icons-library/social-media/instagram';
import { LinkedinIcon } from '../../icons-library/social-media/linkedin';
import { TwitterIcon } from '../../icons-library/social-media/twitter';
import atosLogotype from '../../../../assets/atos-logotype.svg?url';
import IntersectionObserver from '../../intersection-observer/intersection-observer';
import { Text } from '../../../ui-components/text/text';

import { removeTrailingSlash } from '../../../tools/remove-trailing-slash';

export const FeedCard = (props: any) => {
    const [background, setBackground] = createSignal(atosLogotype);
    const [useBackgroundFallback, setUseBackgroundFallback] = createSignal(true);

    const hasExpired = (expiresAt: string) => {
        if (!expiresAt) {
            return false;
        }

        const expirationDate = new Date(expiresAt);
        return expirationDate < new Date();
    };

    const showImage = (visible: boolean | DOMElement) => {
        if (!visible || !props?.image?.url || (props?.image?.expiresAt && hasExpired(props?.image?.expiresAt))) {
            return;
        }

        setUseBackgroundFallback(false);
        setBackground(props?.image?.url);
    };

    // We use the intersection observer here to defer images so they arent loaded until the component is visible,
    // oherwise, we get a penalty from lighthouse because of old image formats that we can control (ince they are external)

    return (
        <ErrorCatcher componentName="Social media feed Card">
            <IntersectionObserver onVisible={showImage} threshold={.1}>
                <Link href={removeTrailingSlash(props.url)} target="_blank" rel="noopener noreferrer">
                    <div
                        class={clsx(
                            'h-full bg-cover bg-no-repeat',
                            '[&:hover_.username]:opacity-100 [&:hover_p]:!text-darkGray',
                            useBackgroundFallback() && 'bg-white bg-[length:50%] bg-center',
                        )}
                        style={{
                            background: background() ? `url(${background()})` : undefined,
                            'background-size': useBackgroundFallback() ? '50%' : 'cover',
                            'background-repeat': 'no-repeat',
                            'background-position': useBackgroundFallback() ? 'center' : undefined,
                            'background-color': useBackgroundFallback() ? '#fff' : undefined,
                        }}
                    >
                        <div class={clsx(
                            !background() && '[&_p]:text-darkGray',
                            background() && '[&_p]:!text-transparent',
                        )}>
                            <div class="px-4 py-6 flex flex-col justify-between h-full box-border">
                                <p class="text-[1.16rem] leading-[130%] line-clamp-6">{props.text}</p>
                                <div class="flex gap-x-[0.71rem] z-10">
                                    <div class="my-auto">
                                        <Switch fallback={<p>icon</p>}>
                                            <Match when={props.icon === 'twitter'}>
                                                <TwitterIcon />
                                            </Match>
                                            <Match when={props.icon === 'facebook'}>
                                                <FacebookIcon />
                                            </Match>
                                            <Match when={props.icon === 'linkedin'}>
                                                <LinkedinIcon />
                                            </Match>
                                            <Match when={props.icon === 'instagram'}>
                                                <InstagramIcon />
                                            </Match>
                                        </Switch>
                                    </div>
                                    <div class="username transition-all duration-200 ease-in-out my-auto opacity-0 [&_h5]:!normal-case">
                                        <Text fontSize="small" color={'darkGray'} noBlockSpacing={true} displayRedVerticalLine={false} fontStyle={'bold'}>
                                            {props.username}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Link>
            </IntersectionObserver>
        </ErrorCatcher>
    );
};
